import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Fetch } from '../utils/Utils.js';
import SectionDescription from '../shared/SectionDescription';
import { validateContact, whatsappLink, readStoredUser, MAX_NAME, MAX_EMAIL, MAX_PHONE, MAX_MESSAGE } from './contactForm';
import './ContactUs.css';

// Codes POST /api/contact answers with as {"error": code}. Fetch (Utils.js)
// turns a known code into its translated text before calling the error
// callback; anything else (a plain-text 4xx/5xx body) gets the generic message.
const API_ERROR_CODES = [
  'contact_name_invalid', 'contact_email_invalid', 'contact_phone_invalid',
  'contact_message_too_short', 'contact_message_too_long',
  'contact_rate_limited', 'contact_send_failed',
];

const ContactUs = () => {
  const { t } = useTranslation();
  const user = readStoredUser();
  const ranchName = localStorage.getItem('zapmanejo_ranch_name') || '';

  // Prefilled from the logged-in user, all editable.
  const [name, setName] = useState(user.name || '');
  const [email, setEmail] = useState(user.email || '');
  const [phone, setPhone] = useState(user.phone_number || '');
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState('');
  const [status, setStatus] = useState('idle'); // idle | sending | sent

  const waLink = whatsappLink(t('contact_whatsapp_prefill'));

  const onSubmit = (ev) => {
    ev.preventDefault();
    if (status === 'sending') return;
    const found = validateContact({ name, email, phone, message });
    setErrors(found);
    setApiError('');
    if (Object.keys(found).length) return;

    setStatus('sending');
    Fetch('/api/contact', 'POST',
      JSON.stringify({ name: name.trim(), email: email.trim(), phone: phone.trim(), message: message.trim() }),
      () => {
        setMessage('');
        setStatus('sent');
      },
      (err) => {
        const known = API_ERROR_CODES.some((code) => t(code) === err);
        setApiError(known ? err : t('contact_send_failed'));
        setStatus('idle');
      },
    );
  };

  const fieldError = (field) => errors[field] && (
    <div className="contact-field-error" role="alert">{t(errors[field])}</div>
  );

  return (
    <div className="contact-page">
      <SectionDescription title={t('contact_us_section_title')} text={t('contact_us_section_text')} />
      <div className="contact-grid">

        <section className="contact-card contact-whatsapp">
          <h2>{t('contact_whatsapp_title')}</h2>
          <p>{t('contact_whatsapp_text')}</p>
          <a className="contact-btn contact-btn-primary" href={waLink} target="_blank" rel="noopener noreferrer">
            {t('contact_whatsapp_button')} ↗
          </a>
        </section>

        {status === 'sent' ? (
          <section className="contact-card contact-sent" role="status">
            <div className="contact-sent-check" aria-hidden="true">✓</div>
            <h2>{t('contact_sent_title')}</h2>
            <p>{t('contact_sent_text')}</p>
            <div className="contact-sent-actions">
              <a className="contact-btn contact-btn-primary" href={waLink} target="_blank" rel="noopener noreferrer">
                {t('contact_whatsapp_button')} ↗
              </a>
              <button type="button" className="contact-btn contact-btn-secondary" onClick={() => setStatus('idle')}>
                {t('contact_send_another')}
              </button>
            </div>
          </section>
        ) : (
          <form className="contact-card contact-form" onSubmit={onSubmit} noValidate>
            <h2>{t('contact_email_title')}</h2>
            <p>{t('contact_email_text')}</p>

            <label htmlFor="contact-name">{t('name')}</label>
            <input id="contact-name" type="text" autoComplete="name" maxLength={MAX_NAME}
              value={name} onChange={(e) => setName(e.target.value)}
              aria-invalid={!!errors.name} />
            {fieldError('name')}

            <label htmlFor="contact-email">{t('email')}</label>
            <input id="contact-email" type="email" autoComplete="email" maxLength={MAX_EMAIL}
              value={email} onChange={(e) => setEmail(e.target.value)}
              aria-invalid={!!errors.email} />
            {fieldError('email')}

            <label htmlFor="contact-phone">{t('phone_number')}</label>
            <input id="contact-phone" type="tel" autoComplete="tel" maxLength={MAX_PHONE}
              value={phone} onChange={(e) => setPhone(e.target.value)}
              aria-invalid={!!errors.phone} />
            {fieldError('phone')}

            <label htmlFor="contact-message">{t('contact_message_label')}</label>
            <textarea id="contact-message" rows={6} maxLength={MAX_MESSAGE}
              placeholder={t('contact_message_placeholder')}
              value={message} onChange={(e) => setMessage(e.target.value)}
              aria-invalid={!!errors.message} />
            {fieldError('message')}

            {ranchName && <div className="contact-note">{t('contact_context_note', { ranch: ranchName })}</div>}
            {apiError && <div className="contact-api-error" role="alert">{apiError}</div>}

            <button type="submit" className="contact-btn contact-btn-primary" disabled={status === 'sending'}>
              {status === 'sending' ? t('sending') : t('send')}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ContactUs;
